import { isTouch, type Controls } from './device';
import type { Input } from './Input';
import { PadMapper, type Dir, type PadContext, type PadSnapshot } from './padmap';

/** What the controller layer needs from the rest of the game. */
export interface GamepadHooks {
  input: Input;
  /** Aim in a mini-game (mouse-pixel equivalents). */
  stageLook(dx: number, dy: number): void;
  /** Menu navigation. */
  nav: {
    ensure(): void;
    move(dir: Dir): void;
    accept(): void;
    scroll(dy: number): void;
  };
  /** The player switched between the controller and keyboard/mouse/touch. */
  onDeviceChange(usingPad: boolean): void;
  /** First button press on a controller (a chance to start audio). */
  onFirstPress(): void;
}

const KEY_NAMES: Record<string, string> = {
  Space: ' ',
  Escape: 'Escape',
  Enter: 'Enter',
  ShiftLeft: 'Shift',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
};

/** A controller was the last thing the player touched. */
let padInUse = false;

/** What to word instructions for: the controller if that's what's in hand, else touch or keys. */
export function currentControls(): Controls {
  return padInUse ? 'pad' : isTouch() ? 'touch' : 'keys';
}

/** Is a game controller plugged in (or has one been used this session)? */
export function controllerConnected(): boolean {
  if (padInUse) return true;
  const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
  return pads.some((p) => p?.connected);
}

/** The `key` a real keyboard would report for a code ('KeyE' -> 'e'), so menus that check `key` work. */
export const keyFor = (code: string): string =>
  KEY_NAMES[code] ?? (code.startsWith('Key') ? code.slice(3).toLowerCase() : code);

/**
 * Game controllers through the Gamepad API. Buttons become the same keyboard events the game
 * already handles (so every menu and mini-game just works); sticks feed movement and look.
 */
export class Gamepads {
  private readonly mapper = new PadMapper();
  private held = new Set<string>();
  private usingPad = false;
  private pressedOnce = false;

  constructor(private readonly hooks: GamepadHooks) {
    // Real keyboard, mouse or touch input hands the prompts and focus rings back.
    const away = (e: Event): void => {
      if (e.isTrusted) this.setUsingPad(false);
    };
    window.addEventListener('keydown', away, true);
    window.addEventListener('pointerdown', away, true);
  }

  get active(): boolean {
    return this.usingPad;
  }

  private setUsingPad(on: boolean): void {
    if (on === this.usingPad) return;
    this.usingPad = on;
    padInUse = on;
    document.documentElement.classList.toggle('pad', on);
    this.hooks.onDeviceChange(on);
  }

  /** The most recently used connected controller. */
  private poll(): PadSnapshot | null {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let best: Gamepad | null = null;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      if (!best || p.timestamp > best.timestamp) best = p;
    }
    if (!best) return null;
    return { buttons: best.buttons.map((b) => b.value || (b.pressed ? 1 : 0)), axes: best.axes };
  }

  /** Call once per frame, before the game reads its input. */
  update(dt: number, ctx: PadContext): void {
    const pad = this.poll();
    const input = this.hooks.input;
    if (!pad) {
      this.release();
      input.pad.x = input.pad.y = 0;
      return;
    }
    const f = this.mapper.update(pad, ctx, dt);
    if (f.active) {
      this.setUsingPad(true);
      if (!this.pressedOnce && pad.buttons.some((v) => v > 0.5)) {
        this.pressedOnce = true;
        this.hooks.onFirstPress();
      }
    }

    for (const code of this.held) if (!f.hold.has(code)) this.key(code, false);
    for (const code of f.hold) if (!this.held.has(code)) this.key(code, true);
    this.held = f.hold;
    for (const code of f.tap) {
      this.key(code, true);
      this.key(code, false);
    }

    input.pad.x = f.move.x;
    input.pad.y = f.move.y;
    if (ctx === 'stage') {
      if (f.look.x || f.look.y) this.hooks.stageLook(f.look.x, f.look.y);
    } else if (f.look.x || f.look.y) input.addLook(f.look.x, f.look.y);
    if (f.zoom) input.wheel += f.zoom;

    if (ctx === 'menu' && this.usingPad) {
      const nav = this.hooks.nav;
      nav.ensure();
      if (f.nav) nav.move(f.nav);
      if (f.accept) nav.accept();
      if (f.scroll) nav.scroll(f.scroll);
    }
  }

  private release(): void {
    for (const code of this.held) this.key(code, false);
    this.held.clear();
  }

  private key(code: string, down: boolean): void {
    window.dispatchEvent(
      new KeyboardEvent(down ? 'keydown' : 'keyup', { code, key: keyFor(code), bubbles: true }),
    );
  }
}
